import * as React from 'react';
import { cn } from '../../lib/utils';
import { ReactComponent as HigloLogoSvg } from '../../assets/signin-visuals/higlo_logo.svg';
import higloIconUrl from '../../assets/account-visuals/higlo_icon.jpg';

/* ────────────────────────────────────────────────────────────────────────────
   v9 illustration primitives.

   HigloLogo: the higlo "H" mark. SVG, takes currentColor.
   HearthIcon: the yellow/dark "Hearth" pill. Inline SVG so we can colour
               and animate (e.g. dim opacity for offline).
   HigloIcon: the warm circular Higlo blob, rendered from a JPG.
   ──────────────────────────────────────────────────────────────────────────── */

/* Logo — pass any width via className (e.g. "w-6 h-6"). Defaults to 24px square.
   Inherits text colour via currentColor for theme flexibility. */
export function HigloLogo({ className, ...rest }) {
  return (
    <HigloLogoSvg
      className={cn('w-6 h-6 text-slate-900', className)}
      aria-hidden="true"
      {...rest}
    />
  );
}

/* Hearth — yellow pill on dark base. Aspect ratio is 131:47.
   Sizes mirror v9: xs ~34w, sm ~44w, md ~60w, lg ~92w. */
const HEARTH_SIZES = {
  xs: 'w-[34px] h-[12px]',
  sm: 'w-[44px] h-[16px]',
  md: 'w-[60px] h-[22px]',
  lg: 'w-[92px] h-[33px]',
};

export function HearthIcon({ size = 'sm', className, dim = false, ...rest }) {
  return (
    <svg
      viewBox="0 0 131 47"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        'inline-block flex-shrink-0',
        HEARTH_SIZES[size] || HEARTH_SIZES.sm,
        dim && 'opacity-55',
        className,
      )}
      aria-hidden="true"
      {...rest}
    >
      {/* Dark base — drawn first so the yellow pill sits on top. */}
      <path
        fill="#3C4858"
        d="M65.2197 17.0484C88.1408 17.0484 107.886 19.0884 116.828 22.023V22.0093C116.828 22.0093 130.156 24.8449 130.439 35.1216C130.447 35.3779 130.441 35.6215 130.432 35.8531C130.434 35.9631 130.439 36.0734 130.439 36.1841C130.439 42.0554 126.631 46.8147 121.933 46.815V46.816H8.50684L8.06934 46.8013C3.57447 46.5167 0 41.8722 0 36.1841C9.01882e-05 32.6373 1.39103 29.4972 3.52734 27.566C6.54208 24.416 10.853 22.8708 12.8301 22.2916C21.3631 19.2136 41.6059 17.0484 65.2197 17.0484Z"
      />
      {/* Yellow pill on top. */}
      <path
        fill="#FFDE5C"
        d="M65.2197 0C88.1408 1.17651e-05 107.886 2.03997 116.828 4.97461V4.96094C116.828 4.96094 130.156 7.79646 130.439 18.0732C130.447 18.3295 130.441 18.5731 130.432 18.8047C130.434 18.9147 130.439 19.025 130.439 19.1357C130.439 25.007 126.631 29.7663 121.933 29.7666V29.7676H8.50684L8.06934 29.7529C3.57447 29.4683 0 24.8238 0 19.1357C8.7946e-05 15.5889 1.39103 12.4488 3.52734 10.5176C6.54208 7.36763 10.853 5.82235 12.8301 5.24316C21.3631 2.16515 41.6059 0 65.2197 0Z"
      />
    </svg>
  );
}

/* Higlo — warm orange circular illustration. Sizes mirror v9. */
const HIGLO_SIZES = {
  xs: 'w-[28px] h-[28px]',
  sm: 'w-[36px] h-[36px]',
  md: 'w-[52px] h-[52px]',
  lg: 'w-[72px] h-[72px]',
};

export function HigloIcon({ size = 'md', className, alt = '', ...rest }) {
  return (
    <img
      src={higloIconUrl}
      alt={alt}
      className={cn(
        'inline-block flex-shrink-0 rounded-full object-cover',
        HIGLO_SIZES[size] || HIGLO_SIZES.md,
        className,
      )}
      {...rest}
    />
  );
}
